import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  type FlightAlertKey,
  ShopFilter,
} from "@b2bportal/air-price-watch-api";
import {
  FlightSearchQueryParams,
  SliceStopCountFilter,
  TripCategory,
} from "@b2bportal/core-types";
import {
  FLIGHT_ROUTES,
  FLIGHT_SHOP_ROUTES,
  FLIGHTS_PATH,
} from "@b2bportal/core-utilities";
import dayjs from "dayjs";
import {
  AirPriceWatchRawEventName,
  useEmitCoreAirPriceWatchEvent,
} from "../../tracking";
import type { WatchActionsComponentProps } from "./WatchActions";
const buildFlightShopQuery = (alertKey: FlightAlertKey) => {
  const formatDateForUrl = (d: string) => dayjs(d).format("YYYY-MM-DD");
  const { filter, origin, returnDate, destination, departureDate } = alertKey;
  const departureDateParam = formatDateForUrl(departureDate);

  let stopsOption = SliceStopCountFilter.ANY_NUMBER;
  let noLCC = false;
  switch (filter) {
    case ShopFilter.NoLCC:
      noLCC = true;
      break;
    case ShopFilter.NonStop:
      stopsOption = SliceStopCountFilter.NONE;
      break;
    case ShopFilter.NonStopNoLCC:
      stopsOption = SliceStopCountFilter.NONE;
      noLCC = true;
      break;
    default:
      stopsOption = SliceStopCountFilter.ANY_NUMBER;
      break;
  }

  const flightShopQueryObject = {
    [FlightSearchQueryParams.ORIGIN]: origin.code,
    [FlightSearchQueryParams.DESTINATION]: destination.code,
    [FlightSearchQueryParams.DEPARTURE_DATE]: departureDateParam,
    ...(returnDate !== null && returnDate !== undefined
      ? {
          [FlightSearchQueryParams.RETURN_DATE]: formatDateForUrl(returnDate),
        }
      : {}),
    [FlightSearchQueryParams.TRIP_CATEGORY]:
      returnDate !== null && returnDate !== undefined
        ? TripCategory.ROUND_TRIP
        : TripCategory.ONE_WAY,
    [FlightSearchQueryParams.STOPS_OPTION]: stopsOption,
    [FlightSearchQueryParams.ADULT_COUNT]: "1",
    [FlightSearchQueryParams.NO_LCC]: noLCC.toString(),
  };

  const shopQuery = new URLSearchParams(flightShopQueryObject).toString();
  return shopQuery;
};

export const useWatchActions = (props: WatchActionsComponentProps) => {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const navigate = useNavigate();

  const trackTappedWatch = useEmitCoreAirPriceWatchEvent(
    AirPriceWatchRawEventName.AirPriceWatchTapped
  );

  const onBookNow = useCallback(() => {
    trackTappedWatch({
      properties: {
        watch: props.watch,
        tripContext: props.tripContext,
      },
    });
    navigate(
      `${FLIGHTS_PATH}/${FLIGHT_ROUTES.SHOP_ROUTE}/${
        FLIGHT_SHOP_ROUTES.DEPARTURE_ROUTE
      }?${buildFlightShopQuery(props.watch.key.value)}`
    );
  }, [navigate, props.watch.key.value]);

  return useMemo(() => {
    return {
      context: {
        cancelOpen,
        changeOpen,
      },
      handlers: {
        setChangeOpen,
        setCancelOpen,
        trackTappedWatch,
        onBookNow,
      },
    };
  }, [cancelOpen, changeOpen, onBookNow]);
};
